import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAccessToken, selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { useSnackbar } from 'notistack'
import { createActionDispatcher } from './core/action-dispatcher'
import { createElaraTransport } from './core/elara-transport'
import { ElaraRealtimeContext } from './ElaraRealtimeProvider'
import type {
    ElaraRealtimeContextType,
    UIAction,
    UIState,
    ActionResult,
    ActionHistoryEntry,
    AudioStreamOptions,
    ConsultationContext,
    DeviceState,
    DiagnosticSection,
    ClientActionEvent,
    StreamingState,
    StreamingPhase,
    ToolCallInfo,
} from './types'

const defaultUIState: UIState = { sections: {} as any }
const defaultConsultationContext: ConsultationContext = { consultationId: '' }
const defaultDeviceState: DeviceState = {
    stethoscopes: [], medicalCameras: [], audioInputs: [], videoInputs: [], customDevices: [],
}
const defaultStreamingState: StreamingState = {
    phase: 'idle',
    streamingText: '',
    toolCalls: [],
}

/**
 * App-root ELARA provider that makes ElaraPanel and all existing ELARA hooks
 * work on every page — not just consultation pages.
 */
export function ElaraGlobalProvider({ children }: { children: React.ReactNode }) {
    const accessToken = useAppSelector(selectAccessToken)
    const authUser = useAppSelector(selectAuthUser)
    const customDevices = useAppSelector(selectCustomDevices)
    const { enqueueSnackbar } = useSnackbar()

    // Refs for stable callback access
    const accessTokenRef = useRef(accessToken)
    useEffect(() => { accessTokenRef.current = accessToken }, [accessToken])

    // State
    const [availableActions, setAvailableActions] = useState<UIAction[]>([])
    const [uiState, setUIState] = useState<UIState>(defaultUIState)
    const [isProcessing, setIsProcessing] = useState(false)
    const [lastResponse, setLastResponse] = useState<string | null>(null)
    const [actionHistory, setActionHistory] = useState<ActionHistoryEntry[]>([])
    const [deviceState, setDeviceState] = useState<DeviceState>(defaultDeviceState)
    const [streamingState, setStreamingState] = useState<StreamingState>(defaultStreamingState)

    // Refs for callbacks
    const actionsRef = useRef(availableActions)
    useEffect(() => { actionsRef.current = availableActions }, [availableActions])
    const uiStateRef = useRef(uiState)
    useEffect(() => { uiStateRef.current = uiState }, [uiState])
    const actionHistoryRef = useRef(actionHistory)
    useEffect(() => { actionHistoryRef.current = actionHistory }, [actionHistory])

    // Action dispatcher
    const actionDispatcher = useMemo(() => createActionDispatcher(), [])

    // ─── Streaming callbacks (stable refs to avoid transport re-creation) ───

    const handlePhaseChange = useCallback((phase: StreamingPhase, error?: string) => {
        setStreamingState(prev => ({
            ...prev,
            phase,
            ...(error ? { error } : {}),
            // Reset on new thinking phase (new message)
            ...(phase === 'thinking' ? { streamingText: '', toolCalls: [], error: undefined } : {}),
        }))
    }, [])

    const handleStreamToken = useCallback((_token: string, accumulated: string) => {
        setStreamingState(prev => ({ ...prev, streamingText: accumulated }))
    }, [])

    const handleToolEvent = useCallback((event: 'call' | 'start' | 'end', tools: Array<{ id: string; name: string; args?: any }>, _toolResult?: string) => {
        setStreamingState(prev => {
            const now = Date.now()
            let updated = [...prev.toolCalls]

            if (event === 'call') {
                // Add new tool calls with 'calling' status
                for (const t of tools) {
                    if (!updated.find(tc => tc.id === t.id)) {
                        updated.push({ id: t.id, name: t.name, status: 'calling', startedAt: now })
                    }
                }
            } else if (event === 'start') {
                // Mark as running
                for (const t of tools) {
                    const existing = updated.find(tc => tc.id === t.id || tc.name === t.name)
                    if (existing) {
                        existing.status = 'running'
                    } else {
                        updated.push({ id: t.id, name: t.name, status: 'running', startedAt: now })
                    }
                }
            } else if (event === 'end') {
                // Mark as done
                for (const t of tools) {
                    const existing = updated.find(tc => tc.id === t.id || tc.name === t.name)
                    if (existing) {
                        existing.status = 'done'
                        existing.finishedAt = now
                    }
                }
            }

            return { ...prev, toolCalls: updated }
        })
    }, [])

    // Transport for text chat (SSE)
    const transport = useMemo(() => {
        if (!authUser?._id) return null
        return createElaraTransport({
            consultationId: '',
            getAccessToken: () => accessTokenRef.current ?? null,
            onProcessingChange: setIsProcessing,
            onResponse: (text: string) => setLastResponse(text),
            onStreamToken: handleStreamToken,
            onPhaseChange: handlePhaseChange,
            onToolEvent: handleToolEvent,
            onClientAction: (event: ClientActionEvent) => {
                const action = actionsRef.current.find(a => a.actionId === event.actionId)
                if (action?.handler) {
                    action.handler(event.params).catch(err => {
                        console.error(`[ElaraGlobal] Action ${event.actionId} failed:`, err)
                    })
                }
            },
        })
    }, [authUser?._id, handlePhaseChange, handleStreamToken, handleToolEvent])

    // Enumerate devices
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices) return
        const enumerate = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                setDeviceState({
                    stethoscopes: [],
                    medicalCameras: [],
                    audioInputs: devices
                        .filter(d => d.kind === 'audioinput')
                        .map(d => ({ deviceId: d.deviceId, label: d.label || 'Microphone', kind: d.kind })),
                    videoInputs: devices
                        .filter(d => d.kind === 'videoinput')
                        .map(d => ({ deviceId: d.deviceId, label: d.label || 'Camera', kind: d.kind })),
                    customDevices: customDevices?.map((d: any) => ({
                        id: d._id, name: d.name, type: d.type, available: true,
                    })) ?? [],
                })
            } catch { /* permissions not granted yet */ }
        }
        enumerate()
    }, [customDevices])

    // ─── Callbacks ───

    const registerActions = useCallback((actions: UIAction[]) => {
        setAvailableActions(prev => {
            const newIds = new Set(actions.map(a => a.actionId))
            return [...prev.filter(a => !newIds.has(a.actionId)), ...actions]
        })
    }, [])

    const unregisterActions = useCallback((actionIds: string[]) => {
        const ids = new Set(actionIds)
        setAvailableActions(prev => prev.filter(a => !ids.has(a.actionId)))
    }, [])

    const updateUIState = useCallback((section: DiagnosticSection, state: Record<string, any>) => {
        setUIState(prev => ({
            sections: { ...prev.sections, [section]: { ...(prev.sections[section] || {}), ...state } },
        }))
    }, [])

    const sendMessage = useCallback((message: string) => {
        if (!transport) return

        // Clear stale response before starting a new request
        setLastResponse(null)

        const path = window.location.pathname
        let pageContext = 'general'
        if (path.includes('meeting-link')) pageContext = 'meeting-links'
        else if (path.includes('planning') || path.includes('agenda')) pageContext = 'planning'
        else if (path.includes('network')) pageContext = 'network'
        else if (path.includes('consultation') || path.includes('diagnostic')) pageContext = 'consultation'
        else if (path.includes('waiting-room')) pageContext = 'waiting-room'
        else if (path.includes('admin')) pageContext = 'admin'
        else if (path.includes('expertise')) pageContext = 'expertise'

        transport.send(message, {
            uiState: uiStateRef.current,
            deviceState,
            pageContext,
            availableActions: actionsRef.current.map(a => ({
                actionId: a.actionId, description: a.description, section: a.section, params: a.params,
            })),
        })
    }, [transport, deviceState])

    const executeAction = useCallback(async (actionId: string, params?: Record<string, any>): Promise<ActionResult> => {
        const action = actionsRef.current.find(a => a.actionId === actionId)
        if (!action?.handler) {
            return { success: false, message: `Action "${actionId}" not found or not registered` }
        }
        try {
            const result = await actionDispatcher.execute(action, params)
            setActionHistory(prev => [...prev, { actionId, params, result, timestamp: Date.now(), undoHandler: action.undoHandler }])
            if (result.success) {
                enqueueSnackbar(result.message || 'Action completed', { variant: 'success', autoHideDuration: 2000 })
            } else {
                enqueueSnackbar(result.message || 'Action failed', { variant: 'error', autoHideDuration: 3000 })
            }
            return result
        } catch (err: any) {
            return { success: false, message: err.message }
        }
    }, [actionDispatcher, enqueueSnackbar])

    const undoLastAction = useCallback(async (): Promise<ActionResult | null> => {
        for (let i = actionHistoryRef.current.length - 1; i >= 0; i--) {
            if (actionHistoryRef.current[i].undoHandler) {
                const result = await actionHistoryRef.current[i].undoHandler!()
                setActionHistory(prev => prev.filter((_, idx) => idx !== i))
                return result
            }
        }
        return null
    }, [])

    const audioStreamOptions = useMemo<AudioStreamOptions>(() => ({
        consultationId: '',
        getAccessToken: () => accessTokenRef.current ?? null,
        getUserId: () => authUser?._id ?? null,
        getEstablishmentId: () => (authUser as any)?.etablissement?._id?.toString() || (authUser as any)?.establishment?._id?.toString() || null,
        getUserName: () => authUser?.userInfo?.name ?? null,
        getUserSurname: () => authUser?.userInfo?.surname ?? null,
        language: authUser?.userInfo?.language ?? 'fr',
        getBusinessMeta: () => ({
            uiState: uiStateRef.current,
            deviceState,
            availableActions: actionsRef.current.map(a => ({
                actionId: a.actionId, description: a.description, section: a.section, params: a.params,
            })),
        }),
    }), [authUser, deviceState])

    const contextValue = useMemo<ElaraRealtimeContextType>(() => ({
        registerActions,
        unregisterActions,
        availableActions,
        uiState,
        updateUIState,
        consultationContext: defaultConsultationContext,
        deviceState,
        sendMessage,
        isProcessing,
        lastResponse,
        streamingState,
        executeAction,
        actionHistory,
        undoLastAction,
        audioStreamOptions,
    }), [
        registerActions, unregisterActions, availableActions,
        uiState, updateUIState, deviceState,
        sendMessage, isProcessing, lastResponse, streamingState,
        executeAction, actionHistory, undoLastAction,
        audioStreamOptions,
    ])

    return (
        <ElaraRealtimeContext.Provider value={contextValue}>
            {children}
        </ElaraRealtimeContext.Provider>
    )
}
